import React, { useState, useRef } from "react";
import {
  Button,
  Popover,
  Tooltip,
  IconButton,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import infoIconSvg from "../../assets/images/UserTable/InfoIcon.svg";
import CloseIcon from "@mui/icons-material/Close";
import { ProfileData } from "../../common/common.model";
import { ImageBadgeName, logout } from "../../common/common";
import { useAppSelector } from "../../hooks/store";
import { updateProjectMappings } from "../../services/common.service";
import orbitLogo from "../../assets/images/orbit_logo.svg";
import orionLogo from "../../assets/images/orion_logo.svg";
import OrionLogo from "../../assets/images/orion_logo.png";
import OrionGreenSvg from "../../assets/images/orion_green_icon.svg";
import SourcinghubTextSvg from "../../assets/images/sourcing_hub_icon.svg";
import OrionGSASvg from "../../assets/Orion-GSA.svg";
import OrionTextSVG from "../../assets/images/orion_toggle_icon.svg";
import OrbitTextSvg from "../../assets/images/orbit_toggle_icon.svg";
interface ProfileButtonProps {
  profileData: ProfileData;
  isOpen?: boolean;
  anchorEl?: HTMLElement | null;
  onOpen?: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  onClose?: () => void;
}

const ProfileButton: React.FC<ProfileButtonProps> = ({
  profileData,
  isOpen = false,
  anchorEl = null,
  onOpen,
  onClose,
}) => {
  const { loading } = useAppSelector(
    (state) => state.rootReducer.userInfoReducer.profileData
  );

  // Use local state only if external control is not provided
  const [localAnchorEl, setLocalAnchorEl] = useState<HTMLElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [selectedToggle, setSelectedToggle] = useState(() => {
    return localStorage.getItem("selectedToggle") || "Orion";
  });
  const [isLoading, setIsLoading] = useState(false);

  // Determine if we're using controlled or uncontrolled behavior
  const usingControlled = onOpen !== undefined && onClose !== undefined;
  const effectiveAnchorEl = usingControlled ? anchorEl : localAnchorEl;
  const effectiveOpen = usingControlled ? isOpen : Boolean(localAnchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    if (usingControlled) {
      if (onOpen) onOpen(event);
    } else {
      setLocalAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    if (usingControlled) {
      if (onClose) onClose();
    } else {
      setLocalAnchorEl(null);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("DATE_TIME_FORMAT");
    logout();
  };

  const handleToggleClick = async (_: any, newToggle: string) => {
    if (newToggle !== null) {
      setSelectedToggle(newToggle);
      localStorage.setItem("selectedToggle", newToggle);
      setIsLoading(true);
      try {
        const response = await updateProjectMappings(newToggle);
        if (response) {
          localStorage.setItem("projectName", response.project_name);
          window.location.reload();
        }
      } catch (error) {
        console.error("Error updating project name:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const id = effectiveOpen ? "profile-popover" : undefined;

  return (
    <>
      <Button
        ref={buttonRef}
        onClick={handleOpen}
        sx={{
          width: 32,
          height: 32,
          backgroundColor: "#3474E0",
          color: "#FFFFFF",
          borderRadius: "50%",
          minWidth: 0,
          padding: 0,
          fontSize: 12,
          fontWeight: "400",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textTransform: "uppercase",
        }}
      >
        <ImageBadgeName
          name={profileData.given_name}
          height="32px"
          width="32px"
          fontSize="12px"
          loading={loading}
        />
      </Button>
      <Popover
        id={id}
        open={effectiveOpen}
        anchorEl={effectiveAnchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={{
          "& .MuiPaper-root": {
            borderRadius: "24px",
            padding: "24px",
            marginLeft: "48px",
          },
        }}
      >
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        {profileData.isPowerUser && (
          <div style={{ position: "absolute", left: 8, top: 8 }}></div>
        )}

        <div style={{ textAlign: "center", paddingTop: "40px" }}>
          <ImageBadgeName
            height="80px"
            width="80px"
            fontSize="40px"
            margin="0 auto"
            loading={loading}
          />
          {profileData.isPowerUser && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginTop: 16,
                gap: "4px",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "400",
                  fontSize: "10px",
                  // color: "#ffffff",
                  // padding: "2px 10px",
                  // background: "#3474E0",
                  // borderRadius: "30px",
                  // width: "fit-content",
                  padding: "6px 8px",
                  background: "#EBF3FF",
                  color: "#5C6063",
                  borderRadius: "4px",
                }}
              >
                Power User
              </Typography>
              <Tooltip
                title="This user has administrative privileges"
                placement="right"
                sx={{
                  bgcolor: "#000000",
                  "& .MuiTooltip-tooltip": {
                    bgcolor: "#000000",
                  },
                }}
              >
                <img src={infoIconSvg} alt="" width={16} height={16} />
              </Tooltip>
            </div>
          )}
          <Typography
            variant="h6"
            sx={{ marginTop: 1, fontWeight: "500", fontSize: "16px" }}
          >
            {profileData.given_name} {profileData.family_name}
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ fontWeight: "400", fontSize: "12px" }}
          >
            {profileData.roles[0].display_name}
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{
              marginTop: 1,
              fontWeight: "400",
              fontSize: "12px",
              color: "#3474E0",
            }}
          >
            {profileData.email}
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            sx={{
              marginTop: 3,
              border: "1px solid #3474E0",
              color: "#3474E0",
              borderRadius: "24px",
              fontWeight: "500",
              fontSize: "12px",
              textTransform: "none",
            }}
            onClick={handleLogout}
          >
            Logout
          </Button>
          <div
            style={{ margin: "16px 0", borderTop: "1px solid #E0E0E0" }}
          ></div>
          <ToggleButtonGroup
            value={selectedToggle}
            exclusive
            onChange={handleToggleClick}
            aria-label="Orion and Orbit Toggle"
            sx={{
              border: "1px solid #ABB1BA",
              borderRadius: "20px",
              "& .MuiToggleButtonGroup-grouped": {
                borderRadius: "20px",
                border: "none",
                "&:not(:first-of-type)": {
                  borderRadius: "20px",
                },
                "&:not(:last-of-type)": {
                  borderRadius: "20px",
                },
              },
              padding: "0.225rem",
              display: "flex",
              alignItems: "center",
            }}
          >
            <ToggleButton
              value="Orbit"
              sx={{
                padding: "6px",
                width: "80px",
                textTransform: "capitalize",
                "&.Mui-selected": {
                  color: "#1976d2",
                  backgroundColor: "rgba(25, 118, 210, 0.08)",
                  border: "1px solid #E4E7EC",
                },
              }}
            >
              <img
                src={OrbitTextSvg}
                alt="Orbit Logo"
                style={{ marginRight: "4px" }}
              />
            </ToggleButton>
            <ToggleButton
              value="Orion"
              sx={{
                padding: "6px",
                width: "80px",
                textTransform: "capitalize",
                "&.Mui-selected": {
                  color: "#1976d2",
                  backgroundColor: "rgba(25, 118, 210, 0.08)",
                  border: "1px solid #E4E7EC",
                },
              }}
            >
              <img
                src={OrionTextSVG}
                alt="Orion Logo"
                style={{ marginRight: "4px" }}
              />
            </ToggleButton>
            <ToggleButton
              value="SourcingHub"
              sx={{
                padding: "6px",
                width: "100px",
                textTransform: "capitalize",
                paddingLeft: "16px",
                paddingTop: "8px",
                "&.Mui-selected": {
                  color: "#1976d2",
                  backgroundColor: "rgba(25, 118, 210, 0.08)",
                  border: "1px solid #E4E7EC",
                  paddingLeft: "16px",
                  paddingTop: "8px",
                },
              }}
            >
              <img
                src={OrionGSASvg}
                alt="Sourcing Hub Logo"
                style={{ marginRight: "4px" }}
              />
            </ToggleButton>
          </ToggleButtonGroup>
        </div>
      </Popover>
    </>
  );
};

export default ProfileButton;
